import { useEffect, useState } from "react";
import {
  SMALL_VIEWPORT,
  RECOMMENDED_OUTFITS_HEADER,
  PAGE_AREAS,
} from "../../constants";
import { PrimaryPrice } from "../priceComponents";
import { COMPLEMENTARY_PRIMARY_PRICE } from "../priceComponents/priceTypes";
import ProductCard from "../productCard/ProductCard";
import { useVariant } from "../../context/variant/VariantContext";
import { useViewport } from "../../context/viewport/ViewportContext";
import { getPrimaryVariant } from "../../utils/product";
import PropTypes from "prop-types";
import styles from "./RecommendedOutfitsProduct.scss";
import saveForLater from "../../utils/saveForLater";
import { getFullImageUrl } from "./utils";
import { fireRecommendedOutfitsAddToBagAnalytics } from "../../analytics/addToBag";
import fireNotifyMeClickAnalytics from "../../analytics/backInStock";
import RecommendedOutfitsSimilarItems from "./RecommendedOutfitsSimilarItems";

//index-1 to match the zero-based index from the first recommended product
//ctaref sets the prop6
//featureref1 sets the eVar60 and eVar61 values for the product
const generatePdpUrlWithCtaRef = (url, index) =>
  url
    ? `${url}#ctaref-styled+for+you+content+-+styled+for+you_${
        index - 1
      }&featureref1-styled+for+you+content+styled+for+you`
    : null;

const getFullVariantIdentifier = (selectedVariant, defaultColourWayId) =>
  selectedVariant
    ? `variantId:${selectedVariant.variantId}`
    : `colourWayId:${defaultColourWayId}`;

export default function RecommendedOutfitsProduct({
  product,
  modalControls,
  index,
  modalOpen,
}) {
  const { selectedVariant, setSelectedVariantId } = useVariant();
  const { viewportCategory } = useViewport();
  const [similarItemsVisible, setSimilarItemsVisible] = useState(false);

  useEffect(() => {
    if (product?.isInStock === false || selectedVariant?.isInStock === false) {
      setSimilarItemsVisible(true);
    }

    if (!modalOpen) {
      setSimilarItemsVisible(false);
      setSelectedVariantId(null);
    }
  }, [selectedVariant, modalOpen, product]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!product) {
    return null;
  }

  const fullImageUrl = getFullImageUrl(product.images[0].url);
  const defaultColourWayId = getPrimaryVariant(product.variants).colourWayId;
  const variantIdentifier = getFullVariantIdentifier(
    selectedVariant,
    defaultColourWayId,
  );

  const handleSaveClick = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const identifier = selectedVariant
      ? { variantId: selectedVariant.variantId }
      : { colourWayId: defaultColourWayId };

    saveForLater({
      product,
      items: [{ productId: product.id, ...identifier }],
      pageArea: PAGE_AREAS.RECOMMENDED_OUTFITS,
    });
  };

  const enhanceNotifyMeClickAnalytics = (selectedVariantId) => {
    fireNotifyMeClickAnalytics(
      selectedVariantId,
      PAGE_AREAS.RECOMMENDED_OUTFITS,
      product,
    );
  };

  return (
    <div className={styles.container}>
      <ProductCard
        key={product.id}
        product={product}
        productLink={generatePdpUrlWithCtaRef(product.pdpUrl, index)}
        variantIdentifier={variantIdentifier}
        variantSelectorId="recommendedOutfitsProductVariantSelector"
        imageWidth={viewportCategory === SMALL_VIEWPORT ? 163 : 218}
        primaryImageUrl={fullImageUrl}
        priceRenderProp={() => (
          <PrimaryPrice
            priceData={product.price}
            testId="product-price"
            priceType={COMPLEMENTARY_PRIMARY_PRICE}
          />
        )}
        scope={RECOMMENDED_OUTFITS_HEADER}
        fireAddToBagAnalytics={fireRecommendedOutfitsAddToBagAnalytics(product)}
        fireNotifyMeClickAnalytics={enhanceNotifyMeClickAnalytics}
        handleSaveClick={handleSaveClick}
        modalControls={modalControls}
      />
      {similarItemsVisible && (
        <RecommendedOutfitsSimilarItems
          product={product}
          fireSimilarItemsAnalytics={
            selectedVariant?.isInStock === false ? true : false
          }
        />
      )}
    </div>
  );
}

RecommendedOutfitsProduct.propTypes = {
  product: PropTypes.shape({
    isInStock: PropTypes.bool.isRequired,
    images: PropTypes.arrayOf({
      isPrimary: PropTypes.bool.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
    price: PropTypes.object.isRequired,
    id: PropTypes.number.isRequired,
    variants: PropTypes.arrayOf(PropTypes.object),
    pdpUrl: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  modalControls: PropTypes.object.isRequired,
  modalOpen: PropTypes.bool.isRequired,
};
